/** "il y a 3 j", "il y a 5 h", "à l’instant" : précision suffisante pour une carte. */
export function ago(iso: string, now = Date.now()): string {
  const minutes = Math.max(
    0,
    Math.round((now - new Date(iso).getTime()) / 60_000),
  );
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

/** « 23 h » sous deux jours, « 9,3 j » au-delà ; « — » si inconnu. */
export function formatDurationHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return '—';
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${(Math.round((hours / 24) * 10) / 10).toLocaleString('fr-FR')} j`;
}

/** Duration between two instants: « 42s », « 3m 05s », « 2h 10m ». */
export function formatDurationBetween(
  startIso: string,
  end: string | number,
): string {
  const endMs = typeof end === 'number' ? end : new Date(end).getTime();
  const ms = endMs - new Date(startIso).getTime();
  if (ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** "reste 2 h 05", "reste 12 min", "expiré". */
export function timeLeft(expiresAt: string | null, now = Date.now()): string {
  if (!expiresAt) return 'expiration inconnue';
  const minutes = Math.floor((new Date(expiresAt).getTime() - now) / 60_000);
  if (minutes <= 0) return 'expiré';
  if (minutes < 60) return `reste ${minutes} min`;
  return `reste ${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(
    2,
    '0',
  )}`;
}

export interface Delta {
  /** Exemple : « ▼ 1,4 j ». */
  text: string;
  /** Whether the change goes the good way; null when there is no change or no baseline. */
  good: boolean | null;
}

/** Change of a duration versus the previous period: lower is better. */
export function durationDelta(
  current: number | null,
  previous: number | null,
): Delta | null {
  if (current === null || previous === null) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.5) return { text: 'stable', good: null };
  return {
    text: `${diff < 0 ? '▼' : '▲'} ${formatDurationHours(Math.abs(diff))}`,
    good: diff < 0,
  };
}

/** Change of a percentage or a count; `higherIsBetter` null for neutral figures. */
export function numberDelta(
  current: number | null,
  previous: number | null,
  unit: string,
  higherIsBetter: boolean | null,
): Delta | null {
  if (current === null || previous === null) return null;
  const diff = current - previous;
  if (diff === 0) return { text: 'stable', good: null };
  return {
    text: `${diff < 0 ? '▼' : '▲'} ${Math.abs(diff)}${unit}`,
    good: higherIsBetter === null ? null : diff > 0 === higherIsBetter,
  };
}
